/**
 * @praesto/core/organization — hotels / departments / users / memberships.
 *
 * Read-only from the client: firestore.rules forbid client writes to
 * users/memberships (server/Cloud Function only). Every path is tenant-scoped
 * via `tenantPath` (fail-closed). Membership id is `${userId}_${hotelId}`.
 *
 * Caching: hotels / users / memberships are de-duplicated with a TTL-based
 * promise cache (default 30s). The Schedule + Templates + WorkloadView views
 * call these on every mount; without dedup, a quick nav loop re-fetches the
 * same collection multiple times. Mutations (handled by Cloud Functions)
 * invalidate cache via `invalidateOrgCache()` exported below.
 *
 * Contract: packages/contracts/src/core-api.ts (OrganizationService).
 */
import type { OrganizationService, Unsubscribe, UsersQuery } from "@praesto/contracts";
import type { Department, Hotel, HotelId, Membership, User, UserId } from "@praesto/shared-types";
import { collection, doc, getDoc, getDocs, onSnapshot, query, where } from "firebase/firestore";
import type { RuntimeContext } from "../internal/context";
import { tenantPath } from "../internal/paths";

export { normalizeDepartments, type DepartmentLabel } from "./normalize-departments";

const ORG_CACHE_TTL_MS = 30_000;

interface CacheEntry<T> {
  promise: Promise<T>;
  expiresAt: number;
}

const cacheStore = new Map<string, CacheEntry<unknown>>();

function cached<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
  const now = Date.now();
  const existing = cacheStore.get(key) as CacheEntry<T> | undefined;
  if (existing && existing.expiresAt > now) return existing.promise;
  const promise = fetcher().catch((e) => {
    // Drop failed entry so the next call retries fresh.
    cacheStore.delete(key);
    throw e;
  });
  cacheStore.set(key, { promise: promise as Promise<unknown>, expiresAt: now + ORG_CACHE_TTL_MS });
  return promise;
}

/**
 * Drop one or all cached organization entries. Call after a successful
 * mutation (membership change, hotel create, etc.) to force the next read
 * to hit Firestore. Without arguments clears everything.
 */
export function invalidateOrgCache(key?: string): void {
  if (key) {
    for (const k of Array.from(cacheStore.keys())) {
      if (k.includes(key)) cacheStore.delete(k);
    }
  } else {
    cacheStore.clear();
  }
}

export function createOrganizationService(ctx: RuntimeContext): OrganizationService {
  const { db } = ctx.firebase;
  const tid = ctx.tenantId;
  const col = (...segs: string[]) => collection(db, tenantPath(tid, ...segs));
  const ref = (...segs: string[]) => doc(db, tenantPath(tid, ...segs));

  const readAll = async <T>(...segs: string[]): Promise<ReadonlyArray<T>> => {
    const snap = await getDocs(col(...segs));
    return snap.docs.map((d) => d.data() as T);
  };
  const readOne = async <T>(...segs: string[]): Promise<T | null> => {
    const snap = await getDoc(ref(...segs));
    return snap.exists() ? (snap.data() as T) : null;
  };

  const cacheKey = (k: string) => `${tid}:${k}`;

  return {
    hotels: () => cached(cacheKey("hotels"), () => readAll<Hotel>("hotels")),
    hotel: (id: HotelId) => readOne<Hotel>("hotels", id),
    departments: (hotelId: HotelId) =>
      cached(cacheKey(`departments:${hotelId}`), () =>
        readAll<Department>("hotels", hotelId, "departments"),
      ),

    async users(opts?: UsersQuery): Promise<ReadonlyArray<User>> {
      const all = await cached(cacheKey("users"), () => readAll<User>("users"));
      if (!opts?.hotelId) return all;
      const ms = await this.membershipsForHotel(opts.hotelId);
      const ids = new Set(ms.map((m) => m.userId));
      return all.filter((u) => ids.has(u.id));
    },
    user: (id: UserId) => readOne<User>("users", id),

    membership: (userId: UserId, hotelId: HotelId) =>
      readOne<Membership>("memberships", `${userId}_${hotelId}`),
    membershipsForUser(userId: UserId): Promise<ReadonlyArray<Membership>> {
      return cached(cacheKey(`memberships:user:${userId}`), async () => {
        const snap = await getDocs(query(col("memberships"), where("userId", "==", userId)));
        return snap.docs.map((d) => d.data() as Membership);
      });
    },
    membershipsForHotel(hotelId: HotelId): Promise<ReadonlyArray<Membership>> {
      return cached(cacheKey(`memberships:hotel:${hotelId}`), async () => {
        const snap = await getDocs(query(col("memberships"), where("hotelId", "==", hotelId)));
        return snap.docs.map((d) => d.data() as Membership);
      });
    },

    subscribeHotels(cb: (hotels: ReadonlyArray<Hotel>) => void): Unsubscribe {
      // Snapshot subscribers refresh the cache opportunistically so the next
      // imperative call returns the latest snapshot data.
      return onSnapshot(col("hotels"), (snap) => {
        const hotels = snap.docs.map((d) => d.data() as Hotel);
        cacheStore.set(cacheKey("hotels"), {
          promise: Promise.resolve(hotels) as Promise<unknown>,
          expiresAt: Date.now() + ORG_CACHE_TTL_MS,
        });
        cb(hotels);
      });
    },
    subscribeUsers(
      _opts: { hotelId?: HotelId },
      cb: (users: ReadonlyArray<User>) => void,
    ): Unsubscribe {
      return onSnapshot(col("users"), (snap) => {
        const users = snap.docs.map((d) => d.data() as User);
        cacheStore.set(cacheKey("users"), {
          promise: Promise.resolve(users) as Promise<unknown>,
          expiresAt: Date.now() + ORG_CACHE_TTL_MS,
        });
        cb(users);
      });
    },
  };
}
